/* eslint-disable react/prop-types */
import { GangResponseDto } from '@operations/app/api'
import { useOperationsStore } from '@operations/AppProvider'

import { useTranslate } from '@tolgee/react'
import {
  ButtonOption,
  ConfirmationDialog,
  OptionsButton,
  TomTable,
  TomTableActionsProps,
  tomTableColumnFilterPresets,
  TomTableEmptyCell,
  useTomTable,
} from '@tom-ui/ui'
import { useTablePreferencesConfig } from '@tom-ui/utils'
import { MRT_ColumnDef } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { GangStore } from '@operations/stores/GangStore'
import { useGangMessageHandler } from '../../hooks/useGangMessageHandler'

interface Props {
  gangStore: GangStore
}

export const GangTable = observer(({ gangStore }: Props) => {
  const { t } = useTranslate()
  const { appStore } = useOperationsStore()
  const { getExceptionMessage, getSuccessMessage } = useGangMessageHandler()
  const navigate = useNavigate()
  const [deleteItems, setDeleteItems] = useState<{ id: number; name: string }[]>([])

  const handleDelete = async () => {
    if (!deleteItems.length) return

    try {
      await gangStore.deleteGangs(deleteItems.map(item => item.id))
      appStore.setShowAlert('success', getSuccessMessage())
      setDeleteItems([])
    } catch (error) {
      appStore.setShowAlert('error', getExceptionMessage(error))
    }
  }

  const columns = useMemo<MRT_ColumnDef<GangResponseDto>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('name', 'Name'),
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        accessorKey: 'description',
        header: t('description', 'Description'),
        Cell: TomTableEmptyCell,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
    ],
    [t],
  )

  const tableActions = useMemo<TomTableActionsProps<GangResponseDto>[]>(
    () => [
      {
        label: t('delete', 'Delete'),
        onClick: rows => {
          const itemsToDelete = rows.map(row => ({
            id: row.original.id,
            name: row.original.name,
          }))
          setDeleteItems(itemsToDelete)
        },
      },
    ],
    [t],
  )

  const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig('operations/gangs')

  const table = useTomTable<GangResponseDto>(
    {
      columns,
      data: gangStore.items,
      enableExpanding: false,
      layoutMode: 'grid',
      renderRowActions: ({ row }) => {
        const options: ButtonOption[] = [
          {
            label: t('edit', 'Edit'),
            onClick: () => navigate(`${row.original.id}`),
            testId: `edit-${row.original.id}`,
          },
          {
            label: t('delete', 'Delete'),
            onClick: () => setDeleteItems([{ id: row.original.id, name: row.original.name }]),
            testId: `delete-${row.original.id}`,
            color: 'error',
          },
        ]

        return (
          <OptionsButton
            tooltip={t('moreOptions', 'More options')}
            options={options}
            position='right'
            testId='gang-options-btn'
          />
        )
      },
      muiTableContainerProps: {
        sx: {
          maxHeight: 'calc(100vh - 202px)',
          minHeight: 'calc(100vh - 202px)',
        },
      },
    },
    tableActions,
    undefined,
    { columns, savedPreferences, onPreferencesChange },
  )

  return (
    <>
      <TomTable table={table} />
      <ConfirmationDialog
        open={deleteItems.length > 0}
        title={
          deleteItems.length === 1
            ? `${t('delete', 'Delete')} ${deleteItems[0].name}`
            : t('delete', 'Delete')
        }
        message={t(
          'deleteGangMessage',
          'Deleting {object} will make it unavailable for selection for operators. Previous recordings with this gang will stay in the system.',
          {
            object:
              deleteItems.length === 1
                ? t('thisItem', 'this item')
                : t('theseItems', 'these items'),
          },
        )}
        primaryActionText={t('yesDelete', 'Yes, delete')}
        closeLabel={t('cancel', 'Cancel')}
        maxWidth='xs'
        onConfirm={() => handleDelete()}
        onClose={() => setDeleteItems([])}
      />
    </>
  )
})
